import { useDisclosure } from '@chakra-ui/react'
import { RoutePaths } from 'containers/Core/Routes'
import { useSourceContext } from 'context/SourceContext/SourceProvider'
import { WithAccountProps } from 'hoc/withAccount'
import useAppointmentDefinitions from 'hooks/useAppointments/useAppointmentDefinitions'
import useAppointmentNotes, {
  AppointmentNote,
} from 'hooks/useAppointments/useAppointmentNotes'
import useMember from 'hooks/useUser/useMember'
import React, { useMemo, useState } from 'react'
import {
  AppointmentModifyEventProps,
  BookingEventSource,
} from 'utils/amplitude.types'
import { Amplitude } from 'utils/amplitude.utils'
import {
  formatAppointmentText,
  isWithin24Hours,
} from 'utils/appointments.utils'
import AppointmentNoteModal from '../MyHealth/PastAppointments/AppointmentNoteModal'
import CalendarModal from './Modals/CalendarModal'
import MultiActionAppointmentCard, {
  ActionProperties,
} from './MultiActionAppointmentCard'
import { Appointment } from 'types/appointments'
import useSourceAppointment from 'hooks/useAppointments/useSourceAppointment'
import useAppointments from 'hooks/useAppointments/useAppointments'

interface AppointmentCardProps {
  appointment: Appointment
}

const AppointmentCard: React.FC<WithAccountProps & AppointmentCardProps> = ({
  appointment,
  user,
}) => {
  const { appointmentDefinitions } = useAppointmentDefinitions()
  const { member } = useMember()
  const { appointmentNotes } = useAppointmentNotes()
  const { source } = useSourceContext()
  const { getAppointmentCode } = useAppointments()

  const {
    appointment: sourceAppointment,
    isLoading: isSourceAppointmentLoading,
  } = useSourceAppointment(appointment.sourceId)

  const note = useMemo(
    () => appointmentNotes?.find((n) => n.appointment.id === appointment.id),
    [appointmentNotes]
  )
  const [showNote, setShowNote] = useState<AppointmentNote | null>(null)
  const [
    amplitudeModifyAppointmentEventProps,
    setAmplitudeModifyAppointmentEventProps,
  ] = useState<AppointmentModifyEventProps | null>(null)

  /*
  We need the same props for AppointmentModifyStart and AppointmentCancelPolicyClick, and building these props requires a source request.
  This allows us to fetch them once and then have the props available for both Modify Appointment & View our cancellation policy w/o needing additional requests
  */
  const fetchAmplitudeModifyAppointmentEventProps = async (): Promise<AppointmentModifyEventProps> => {
    if (
      amplitudeModifyAppointmentEventProps &&
      amplitudeModifyAppointmentEventProps.appointment_type_name
    ) {
      return amplitudeModifyAppointmentEventProps
    }

    let appointmentTypeName: string

    try {
      const appointmentType = await source.scheduling.appointmentTypes.retrieve(
        appointment?.type.id
      )
      appointmentTypeName = appointmentType?.name ?? ''
    } catch (error) {
      console.error('Error retrieving appointment type', error)
      appointmentTypeName = ''
    }

    const amplitudeEventProps: AppointmentModifyEventProps = {
      appointment_id: appointment.sourceId,
      appointment_type_name: appointmentTypeName,
      appointment_time: appointment.time,
    }
    setAmplitudeModifyAppointmentEventProps(amplitudeEventProps)
    return amplitudeEventProps
  }

  const hideShowNoteDialog = () => setShowNote(null)

  const handleOpenNote = () => {
    if (note) {
      setShowNote(note)
      Amplitude.appointmentNoteOpen({
        appointment_note_id: note.id,
        appointment_id: appointment.id,
        appointment_type_name: note.appointment.type.category,
        source: 'dashboard',
      })
    }
  }

  const {
    isOpen: isCalendarModalOpen,
    onOpen: calendarModalOnOpen,
    onClose: calendarModalOnClose,
  } = useDisclosure()

  const isAppointmentModifiable = useMemo(
    () => (appointment ? !isWithin24Hours(appointment.time) : false),
    [appointment]
  )

  const code = getAppointmentCode(appointment)

  // timzeone can be both null or undefined. Just shorten to undefined
  const memberTimezone = member?.time_zone || undefined

  const appointmentDefinition = useMemo(() => {
    return appointmentDefinitions?.find((d) => d.id === code)
  }, [appointmentDefinitions])

  const handleModifyAppointment = async () => {
    const amplitudeEventProps = await fetchAmplitudeModifyAppointmentEventProps()
    Amplitude.appointmentModifyStart(amplitudeEventProps)
    window.location.href = `${RoutePaths.SCHEDULING}?appointment=${appointment.sourceId}&source=${BookingEventSource.RESCHEDULE}`
  }

  const appointmentAction: ActionProperties = {
    buttonText: appointmentDefinition?.buttonText ?? '',
    disableButton: false,
    handleClick: handleModifyAppointment,
    isLink: false,
  }

  const calendarAction: ActionProperties = {
    buttonText: 'Add to Calendar',
    disableButton: false,
    handleClick: () => calendarModalOnOpen(),
    isLink: true,
  }

  const appointmentSummaryAction: ActionProperties = {
    buttonText: 'Appointment Summary',
    disableButton: false,
    handleClick: () => handleOpenNote(),
  }

  const getButtonActions = () => {
    if (code === 'scheduled' && isAppointmentModifiable) {
      // Don't show modification actions while loading source appointment data
      if (isSourceAppointmentLoading) {
        return []
      }

      // Only hide modification actions for confirmed appointments
      if (sourceAppointment?.status === 'confirmed') {
        return []
      }

      return [appointmentAction, calendarAction]
    } else if (code == 'complete') {
      return note ? [appointmentSummaryAction] : []
    } else {
      return []
    }
  }

  const actions = appointmentDefinition?.buttonText ? getButtonActions() : []

  if (!appointmentDefinition) {
    return <></>
  } else {
    const title = formatAppointmentText(
      appointmentDefinition.title,
      appointment
    )
    let description = formatAppointmentText(
      appointmentDefinition.description,
      appointment
    )

    /// renderCardDescription looks for \\n to add a <br>
    if (!note && getAppointmentCode(appointment) === 'complete') {
      description =
        description +
        '\\n' +
        'A summary of your appointment will be available here shortly.'
    }

    return (
      <>
        <MultiActionAppointmentCard
          icon={appointmentDefinition.icon}
          title={title}
          description={description}
          actions={actions}
          isScheduled={code === 'scheduled'}
          onViewCancellationPolicy={fetchAmplitudeModifyAppointmentEventProps}
        />
        <CalendarModal
          isOpen={isCalendarModalOpen}
          onClose={calendarModalOnClose}
          appointment={appointment}
          timeZone={memberTimezone}
        />
        {showNote && (
          <AppointmentNoteModal
            note={showNote}
            onHide={hideShowNoteDialog}
            openFrom={BookingEventSource.APPOINTMENT_NOTE}
            user={user}
            labsOrdered={showNote.labsOrdered}
          />
        )}
      </>
    )
  }
}

export default AppointmentCard
